import React, { useState } from 'react';
import { X, Home, Users, ShoppingCart, Package, Store, Wrench, UserCog, BarChart3, CheckSquare, KeyRound, Calendar, Component, MapPinHouse, XCircle, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentRentalPoint } from '@/stores/useCurrentRentalPoint';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface HeaderMobileMenuProps {
  onClose: () => void;
}

// Mobile Navigation Content Component
const MobileNavContent: React.FC<{
  onClose: () => void;
}> = ({
  onClose
}) => {
  const [rentalSubmenuOpen, setRentalSubmenuOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;
  const toggleRentalSubmenu = () => setRentalSubmenuOpen(!rentalSubmenuOpen);
  const menuItems = [{
    title: 'Главная',
    path: '/main',
    icon: Home
  },
  // Moved Аренда to be after Главная in the sidebar order
  // Rental menu is rendered separately below
  {
    title: 'Клиенты',
    path: '/clients',
    icon: Users
  }, {
    title: 'Заказы',
    path: '/orders',
    icon: ShoppingCart
  }, {
    title: 'Инвентарь',
    path: '/inventory',
    icon: Package
  }, {
    title: 'Магазин',
    path: '/store',
    icon: Store
  }, {
    title: 'Мастерская',
    path: '/workshop',
    icon: Wrench
  }, {
    title: 'Персонал',
    path: '/staff',
    icon: UserCog
  }, {
    title: 'Отчёты',
    path: '/reports',
    icon: BarChart3
  }, {
    title: 'Задачи',
    path: '/tasks',
    icon: CheckSquare
  }];
  const rentalSubItems = [{
    title: 'Календарь',
    path: '/rental-calendar',
    icon: Calendar
  }, {
    title: 'Комплекты',
    path: '/rental-kits',
    icon: Component
  }, {
    title: 'Кросспункт',
    path: '/cross-location',
    icon: MapPinHouse
  }, {
    title: 'Отказы',
    path: '/rental-denials',
    icon: XCircle
  }];
  return <nav className="flex flex-col px-6 pt-6 space-y-1 pb-20">
      {/* Main menu item */}
      {menuItems.slice(0, 1).map(item => {
      const isActive = currentPath === item.path;
      return <Link key={item.path} to={item.path} className={cn('flex items-center px-4 py-4 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors', isActive && 'bg-blue-50 text-blue-600')} onClick={onClose}>
            {React.createElement(item.icon, {
          size: 24,
          className: cn("mr-4 flex-shrink-0", isActive && 'text-blue-600')
        })}
            <span className="text-lg font-medium">{item.title}</span>
          </Link>;
    })}

      {/* Rental menu with submenu - moved to be right after main menu */}
      <div className="relative w-full">
        <div className={cn('flex items-center justify-between w-full rounded-xl transition-colors', (currentPath.includes('/rental-') || currentPath.includes('/cross-location')) && 'bg-blue-50 text-blue-600')}>
          {/* Main rental link */}
          <Link 
            to="/rentals" 
            className={cn('flex items-center flex-1 px-4 py-4 rounded-l-xl text-gray-700 hover:bg-gray-50 transition-colors', (currentPath === '/rentals' || currentPath.includes('/rental-') || currentPath.includes('/cross-location')) && 'bg-blue-50 text-blue-600')}
            onClick={onClose}
          >
            <KeyRound size={24} className="mr-4 flex-shrink-0" />
            <span className="text-lg font-medium">Аренда</span>
          </Link>
          
          {/* Submenu toggle button */}
          <button 
            onClick={toggleRentalSubmenu} 
            className={cn('px-3 py-4 rounded-r-xl text-gray-700 hover:bg-gray-50 transition-colors border-l border-gray-200', (currentPath.includes('/rental-') || currentPath.includes('/cross-location')) && 'bg-blue-50 text-blue-600 border-blue-200')}
          >
            {rentalSubmenuOpen ? <ChevronUp size={20} className="flex-shrink-0" /> : <ChevronDown size={20} className="flex-shrink-0" />}
          </button>
        </div>
        
        {rentalSubmenuOpen && <div className="ml-12 space-y-1 mt-2">
            {rentalSubItems.map(subItem => {
          const isSubActive = currentPath === subItem.path;
          return <Link key={subItem.path} to={subItem.path} className={cn('flex items-center px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors', isSubActive && 'bg-blue-50 text-blue-600')} onClick={onClose}>
                  {React.createElement(subItem.icon, {
              size: 20,
              className: cn("mr-3 flex-shrink-0", isSubActive && 'text-blue-600')
            })}
                  <span className="font-medium">{subItem.title}</span>
                </Link>;
        })}
          </div>}
      </div>
      
      {/* Rest of menu items */}
      {menuItems.slice(1).map(item => {
      const isActive = currentPath === item.path;
      return <Link key={item.path} to={item.path} className={cn('flex items-center px-4 py-4 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors', isActive && 'bg-blue-50 text-blue-600')} onClick={onClose}>
            {React.createElement(item.icon, {
          size: 24,
          className: cn("mr-4 flex-shrink-0", isActive && 'text-blue-600')
        })}
            <span className="text-lg font-medium">{item.title}</span>
          </Link>;
    })}
    </nav>;
};
export const HeaderMobileMenu: React.FC<HeaderMobileMenuProps> = ({
  onClose
}) => {
  const {
    currentRentalPoint
  } = useCurrentRentalPoint();
  return <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header with flex layout matching desktop style */}
      <div className="flex justify-between items-center p-2 border-b h-16 px-[16px]">
        {/* Left section */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" onClick={onClose} className="h-12 w-12 p-0 flex items-center justify-center">
            <X className="h-5 w-5" />
          </Button>
          
          {/* Location selector button styled like desktop version */}
          <Button variant="outline" className="h-12 px-4 rounded-xl border-gray-200 bg-white text-sm flex items-center">
            <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {currentRentalPoint?.name || 'Пункт #1'}
            </span>
          </Button>
        </div>
        
        {/* Right section */}
        <div className="flex items-center">
          <img src="/arendaplus-logo-mini.svg" alt="A+" className="h-6 w-6" />
        </div>
      </div>
      
      {/* Содержимое меню */}
      <div className="flex-1 overflow-y-auto">
        <MobileNavContent onClose={onClose} />
      </div>
    </div>;
};
